import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import Layout from "@/components/Layout";
import { api, fmtBRL, fmtDate } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Upload, Download, Trash2, ArrowLeft } from "lucide-react";
import { toast } from "sonner";

const statusBadge = (s) => {
  const map = {
    recebido: "bg-emerald-100 text-emerald-700 border-emerald-200",
    pendente: "bg-amber-100 text-amber-700 border-amber-200",
    parcial: "bg-blue-100 text-blue-700 border-blue-200",
    atrasado: "bg-red-100 text-red-700 border-red-200",
    cancelado: "bg-gray-100 text-gray-700 border-gray-200",
  };
  return <Badge variant="outline" className={`${map[s] || ""} capitalize text-[10px]`}>{s}</Badge>;
};

export default function ClientCentral() {
  const { id } = useParams();
  const [data, setData] = useState(null);

  const load = async () => {
    const { data: d } = await api.get(`/clients/${id}/central`);
    setData(d);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  const upload = async (file) => {
    const fd = new FormData();
    fd.append("file", file);
    try {
      await api.post(`/files/upload?cliente_id=${id}`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Arquivo enviado");
      load();
    } catch (e) { toast.error("Erro no upload"); }
  };

  const download = async (file) => {
    try {
      const res = await api.get(`/files/${file.id}/download`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = file.original_filename;
      a.click(); URL.revokeObjectURL(url);
    } catch { toast.error("Erro no download"); }
  };

  const deleteFile = async (file) => {
    await api.delete(`/files/${file.id}`); toast.success("Arquivo removido"); load();
  };

  if (!data) return <Layout title="Cliente"><div>Carregando...</div></Layout>;
  const c = data.cliente;

  return (
    <Layout title={c.nome} subtitle="Central do Cliente 360°">
      <Link to="/clientes"><Button variant="ghost" size="sm" className="mb-4"><ArrowLeft className="w-4 h-4 mr-1"/>Voltar</Button></Link>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-4">
        <Card className="p-4 rounded-sm">
          <p className="text-[10px] uppercase tracking-[0.18em] font-bold text-muted-foreground">Valor Contratado</p>
          <p className="kpi-num text-2xl mt-2">{fmtBRL(data.total_contratado)}</p>
        </Card>
        <Card className="p-4 rounded-sm">
          <p className="text-[10px] uppercase tracking-[0.18em] font-bold text-muted-foreground">Recebido</p>
          <p className="kpi-num text-2xl mt-2 text-emerald-700">{fmtBRL(data.total_recebido)}</p>
        </Card>
        <Card className="p-4 rounded-sm">
          <p className="text-[10px] uppercase tracking-[0.18em] font-bold text-muted-foreground">Pendente</p>
          <p className="kpi-num text-2xl mt-2">{fmtBRL(data.total_pendente)}</p>
        </Card>
        <Card className="p-4 rounded-sm">
          <p className="text-[10px] uppercase tracking-[0.18em] font-bold text-muted-foreground">Situação</p>
          <p className="mt-2">{data.inadimplente ? <Badge className="bg-red-100 text-red-700 border-red-200">Inadimplente</Badge> : <Badge className="bg-emerald-100 text-emerald-700 border-emerald-200">Em dia</Badge>}</p>
        </Card>
      </div>

      <Tabs defaultValue="dados" className="w-full">
        <TabsList>
          <TabsTrigger value="dados" data-testid="tab-dados">Dados</TabsTrigger>
          <TabsTrigger value="vendas">Contratos</TabsTrigger>
          <TabsTrigger value="parcelas">Parcelas</TabsTrigger>
          <TabsTrigger value="recebimentos">Recebimentos</TabsTrigger>
          <TabsTrigger value="arquivos">Documentos</TabsTrigger>
          <TabsTrigger value="historico">Histórico</TabsTrigger>
        </TabsList>

        <TabsContent value="dados">
          <Card className="p-6 rounded-sm">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-x-6 gap-y-3 text-sm">
              {[
                ["Nome", c.nome], ["CPF", c.cpf], ["RG", c.rg], ["Nascimento", c.nascimento],
                ["Estado civil", c.estado_civil], ["Telefone", c.telefone], ["WhatsApp", c.whatsapp],
                ["E-mail", c.email], ["CEP", c.cep], ["Endereço", `${c.endereco || ""} ${c.numero || ""}`],
                ["Bairro", c.bairro], ["Cidade/UF", c.cidade ? `${c.cidade}/${c.estado}` : ""],
              ].map(([l, v]) => (
                <div key={l}>
                  <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-muted-foreground">{l}</div>
                  <div className="mt-0.5">{v || "—"}</div>
                </div>
              ))}
            </div>
          </Card>
        </TabsContent>

        <TabsContent value="vendas">
          <Card className="rounded-sm overflow-hidden">
            <Table>
              <TableHeader className="bg-secondary/60"><TableRow>
                <TableHead>Nº</TableHead><TableHead>Data</TableHead><TableHead>Valor</TableHead><TableHead>Forma</TableHead>
              </TableRow></TableHeader>
              <TableBody>{data.vendas.map(v => (
                <TableRow key={v.id}><TableCell>{v.numero}</TableCell><TableCell>{fmtDate(v.data)}</TableCell><TableCell>{fmtBRL(v.valor)}</TableCell><TableCell className="capitalize">{v.forma_pagamento}</TableCell></TableRow>
              ))}{data.vendas.length === 0 && <TableRow><TableCell colSpan={4} className="text-center text-muted-foreground py-6">Sem vendas</TableCell></TableRow>}</TableBody>
            </Table>
          </Card>
        </TabsContent>

        <TabsContent value="parcelas">
          <Card className="rounded-sm overflow-hidden">
            <Table>
              <TableHeader className="bg-secondary/60"><TableRow><TableHead>Parcela</TableHead><TableHead>Vencimento</TableHead><TableHead>Valor</TableHead><TableHead>Recebido</TableHead><TableHead>Status</TableHead></TableRow></TableHeader>
              <TableBody>{data.parcelas.map(p => (
                <TableRow key={p.id}><TableCell>#{p.parcela}</TableCell><TableCell>{fmtDate(p.vencimento)}</TableCell><TableCell>{fmtBRL(p.valor)}</TableCell><TableCell>{fmtBRL(p.valor_recebido)}</TableCell><TableCell>{statusBadge(p.status)}</TableCell></TableRow>
              ))}{data.parcelas.length === 0 && <TableRow><TableCell colSpan={5} className="text-center text-muted-foreground py-6">Sem parcelas</TableCell></TableRow>}</TableBody>
            </Table>
          </Card>
        </TabsContent>

        <TabsContent value="recebimentos">
          <Card className="rounded-sm overflow-hidden">
            <Table>
              <TableHeader className="bg-secondary/60"><TableRow><TableHead>Data</TableHead><TableHead>Valor</TableHead><TableHead>Forma</TableHead></TableRow></TableHeader>
              <TableBody>{data.recebimentos.map(p => (
                <TableRow key={p.id}><TableCell>{fmtDate(p.data)}</TableCell><TableCell>{fmtBRL(p.valor)}</TableCell><TableCell className="capitalize">{p.forma_pagamento}</TableCell></TableRow>
              ))}{data.recebimentos.length === 0 && <TableRow><TableCell colSpan={3} className="text-center text-muted-foreground py-6">Sem recebimentos</TableCell></TableRow>}</TableBody>
            </Table>
          </Card>
        </TabsContent>

        <TabsContent value="arquivos">
          <Card className="p-4 rounded-sm">
            <label className="inline-flex items-center gap-2 mb-3 cursor-pointer">
              <input data-testid="file-upload" type="file" hidden onChange={(e) => e.target.files[0] && upload(e.target.files[0])} />
              <span className="inline-flex items-center gap-2 px-3 py-1.5 bg-primary text-primary-foreground rounded-sm text-sm hover:bg-primary/90"><Upload className="w-4 h-4"/>Anexar</span>
            </label>
            <Table>
              <TableHeader className="bg-secondary/60"><TableRow><TableHead>Nome</TableHead><TableHead>Data</TableHead><TableHead>Por</TableHead><TableHead className="text-right">Ações</TableHead></TableRow></TableHeader>
              <TableBody>{data.arquivos.map(f => (
                <TableRow key={f.id}><TableCell>{f.original_filename}</TableCell><TableCell>{fmtDate(f.created_at)}</TableCell><TableCell>{f.uploaded_by_name}</TableCell><TableCell className="text-right">
                  <Button variant="ghost" size="sm" onClick={() => download(f)}><Download className="w-4 h-4"/></Button>
                  <Button variant="ghost" size="sm" onClick={() => deleteFile(f)}><Trash2 className="w-4 h-4"/></Button>
                </TableCell></TableRow>
              ))}{data.arquivos.length === 0 && <TableRow><TableCell colSpan={4} className="text-center text-muted-foreground py-6">Nenhum arquivo</TableCell></TableRow>}</TableBody>
            </Table>
          </Card>
        </TabsContent>

        <TabsContent value="historico">
          <Card className="p-4 rounded-sm">
            <div className="space-y-2">
              {(c.history || []).map((h, i) => (
                <div key={i} className="flex items-start gap-3 border-l-2 border-primary pl-3 py-1">
                  <div>
                    <div className="text-sm">{h.action}</div>
                    <div className="text-xs text-muted-foreground">{fmtDate(h.timestamp)} • {h.user}</div>
                  </div>
                </div>
              ))}
            </div>
          </Card>
        </TabsContent>
      </Tabs>
    </Layout>
  );
}
